"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { ChevronRight, ChevronLeft, Loader2, Warehouse, ShoppingBag, FileText } from "lucide-react";

interface Category {
  id: string;
  name: string;
  slug: string;
  black_picture?: string | null;
}

interface CategoryWithChildren {
  category: Category;
  children: Category[];
}

interface MenuSection {
  type: "location" | "achat" | "acte-administratif";
  label: string;
  shortLabel: string;
  baseHref: string;
  icon: React.ElementType;
  color: string;
  bgColor: string;
}

const menuSections: MenuSection[] = [
  { type: "location", label: "Location de matériel", shortLabel: "Location", baseHref: "/categories/location", icon: Warehouse, color: "text-secondary", bgColor: "bg-secondary/10" },
  { type: "achat", label: "Achat de matériel", shortLabel: "Achat", baseHref: "/categories/achat", icon: ShoppingBag, color: "text-secondary", bgColor: "bg-secondary/10" },
  { type: "acte-administratif", label: "Acte administratif", shortLabel: "Administratif", baseHref: "/categories/acte-administratif", icon: FileText, color: "text-secondary", bgColor: "bg-secondary/10" },
];

export function MobileMenu({ onNavigate }: { onNavigate?: () => void }) {
  const [level, setLevel] = useState<"root" | "section" | "subcategory">("root");
  const [activeSection, setActiveSection] = useState<MenuSection | null>(null);
  const [activeParent, setActiveParent] = useState<CategoryWithChildren | null>(null);
  const [categories, setCategories] = useState<CategoryWithChildren[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!activeSection) return;
    setLoading(true);
    fetch(`/api/categories?type=${activeSection.type}`)
      .then((res) => res.json())
      .then((data) => setCategories(data.categories || []))
      .catch(() => setCategories([]))
      .finally(() => setLoading(false));
  }, [activeSection]);

  const openSection = (section: MenuSection) => {
    setActiveSection(section);
    setLevel("section");
  };

  const openSubcategory = (cat: CategoryWithChildren) => {
    setActiveParent(cat);
    setLevel("subcategory");
  };

  const goBack = () => {
    if (level === "subcategory") {
      setActiveParent(null);
      setLevel("section");
    } else if (level === "section") {
      setActiveSection(null);
      setCategories([]);
      setLevel("root");
    }
  };

  // Root level — 3 big cards
  if (level === "root") {
    return (
      <div className="flex flex-col gap-2">
        {menuSections.map((section) => (
          <button
            key={section.type}
            onClick={() => openSection(section)}
            className="flex items-center gap-3 p-3.5 rounded-xl border border-gray-100 hover:border-secondary/30 hover:shadow-sm transition-all cursor-pointer text-left"
          >
            <div className={`h-10 w-10 rounded-lg ${section.bgColor} flex items-center justify-center flex-shrink-0`}>
              <section.icon className={`h-5 w-5 ${section.color}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900">{section.label}</p>
            </div>
            <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
          </button>
        ))}
        <Link
          href="/categories"
          onClick={onNavigate}
          className="text-center text-sm text-secondary font-medium py-2 hover:underline"
        >
          Toutes les catégories
        </Link>
      </div>
    );
  }

  // Section level — parent categories list
  if (level === "section" && activeSection) {
    return (
      <div className="flex flex-col">
        {/* Back + section title */}
        <button
          onClick={goBack}
          className="flex items-center gap-2 py-2 text-sm text-secondary font-medium cursor-pointer"
        >
          <ChevronLeft className="h-4 w-4" />
          Retour
        </button>

        <div className={`flex items-center gap-3 px-3 py-3 rounded-xl ${activeSection.bgColor} mb-3`}>
          <activeSection.icon className={`h-5 w-5 ${activeSection.color}`} />
          <Link
            href={activeSection.baseHref}
            onClick={onNavigate}
            className="text-base font-bold text-gray-900 hover:text-secondary"
          >
            {activeSection.label}
          </Link>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-secondary" />
          </div>
        ) : (
          <div className="flex flex-col gap-0.5">
            {categories.map(({ category, children }) => (
              <div key={category.id}>
                {children.length > 0 ? (
                  <button
                    onClick={() => openSubcategory({ category, children })}
                    className="flex items-center justify-between w-full px-3 py-2.5 hover:bg-gray-50 rounded-lg cursor-pointer text-left transition-colors"
                  >
                    <span className="flex items-center gap-2.5 text-sm font-medium text-gray-800">
                      {category.black_picture && (
                        <img src={category.black_picture} alt="" className="w-5 h-5 object-contain opacity-70" />
                      )}
                      {category.name}
                    </span>
                    <ChevronRight className="h-4 w-4 text-gray-300" />
                  </button>
                ) : (
                  <Link
                    href={`/categories/${category.slug}?type=${activeSection.type}`}
                    onClick={onNavigate}
                    className="flex items-center gap-2.5 px-3 py-2.5 hover:bg-gray-50 rounded-lg text-sm font-medium text-gray-800 transition-colors"
                  >
                    {category.black_picture && (
                      <img src={category.black_picture} alt="" className="w-5 h-5 object-contain opacity-70" />
                    )}
                    {category.name}
                  </Link>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Subcategory level — children list
  if (level === "subcategory" && activeParent) {
    return (
      <div className="flex flex-col">
        {/* Back */}
        <button
          onClick={goBack}
          className="flex items-center gap-2 py-2 text-sm text-secondary font-medium cursor-pointer"
        >
          <ChevronLeft className="h-4 w-4" />
          Retour
        </button>

        {/* Parent header */}
        <div className="px-3 py-3 mb-2">
          <Link
            href={`/categories/${activeParent.category.slug}${activeSection ? `?type=${activeSection.type}` : ""}`}
            onClick={onNavigate}
            className="text-base font-bold text-gray-900 hover:text-secondary"
          >
            {activeParent.category.name}
          </Link>
          <Link
            href={`/categories/${activeParent.category.slug}${activeSection ? `?type=${activeSection.type}` : ""}`}
            onClick={onNavigate}
            className="block text-xs text-secondary font-medium mt-1 hover:underline"
          >
            Voir tous les produits
          </Link>
        </div>

        <div className="h-px bg-gray-100 mb-1" />

        <div className="flex flex-col gap-0.5">
          {activeParent.children.map((child) => (
            <Link
              key={child.id}
              href={`/categories/${child.slug}${activeSection ? `?type=${activeSection.type}` : ""}`}
              onClick={onNavigate}
              className="px-3 py-2.5 hover:bg-gray-50 rounded-lg text-sm text-gray-700 transition-colors"
            >
              {child.name}
            </Link>
          ))}
        </div>
      </div>
    );
  }

  return null;
}
